import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';

import {
    Autosuggest,
    SpaceBetween,
    TokenGroup
} from '@cloudscape-design/components';

import { useGlobalState } from '../../pages/context/GlobalStateContext';
import { ItemEquipment } from '../../resources/types';

interface SelectEquipmentsProps {
    isBookCapable?: number;
    isClub?: number;
    isMant?: number;

    idCategory?: number;
    idGroupType?: number;
    idOwner?: number;
    groupTypeMasterCode?: string;

    value?: number[];                       // return ONLY ids
    onConfirm: (selectedIds: number[]) => void;
}

export type SelectEquipmentsHandle = {
    focus: () => void;
};

const SelectEquipments = forwardRef<SelectEquipmentsHandle, SelectEquipmentsProps>(
    (
        {
            isBookCapable = -1,
            isClub = -1,
            isMant = -1,
            idCategory = 0,
            idGroupType = 0,
            idOwner = 0,
            onConfirm,
            value = []
        },
        ref
    ) => {
        const { data } = useGlobalState();

        const inputRef = useRef<HTMLInputElement>(null);

        useImperativeHandle(ref, () => ({
            focus: () => {
                inputRef.current?.focus();
            }
        }));


        const [equipments, setEquipments] = useState<ItemEquipment[]>([]);
        const [selected, setSelected] = useState<ItemEquipment[]>([]);
        const [keyw, setKeyw] = useState("");

        // ---------------------------------------------------------
        // LOAD EQUIPMENT LIST
        // ---------------------------------------------------------
        useEffect(() => {
            if (data?.equipments) {
                const list = data.equipments
                    .filter((e: any) =>
                        (idCategory <= 0 || e.idCategory === idCategory) &&
                        (idGroupType <= 0 || e.idGroupType === idGroupType) &&
                        (idOwner <= 0 || e.jsonOwners.contains(idOwner))
                    )
                    .map((e: any) => ({
                        idEquipment: e.idEquipment,
                        registration: e.registration,
                        equipment: e.equipment,
                        isTowPlane: e.isTowPlane,
                        groupType: e.groupType,
                        groupTypeMasterCode: e.groupTypeMasterCode,
                        make: e.make,
                        category: e.category,
                        idCategory: e.idCategory,
                        isBookCapable: e.isBookCapable,
                        model: e.model,
                        isClub: e.isClub,
                        isMant: e.isMant,
                        value: e.idEquipment.toString(),
                        label: e.registration,
                        description: `${e.make} ${e.model}`,
                        labelTag: e.category,
                        jsonOwners: e.jsonOwners,
                        jsonSII: e.jsonSII,
                        jsonAltitudes: e.jsonAltitudes
                    }));

                setEquipments(list);
            }
        }, [data?.equipments]);

        // ---------------------------------------------------------
        // INITIAL VALUES (multiple)
        // ---------------------------------------------------------
        useEffect(() => {
            if (value.length > 0 && equipments.length > 0) {
                const matched = equipments.filter(eq =>
                    value.includes(eq.idEquipment)
                );
                setSelected(matched);
            }
        }, [value, equipments]);




        // ---------------------------------------------------------
        // AUTOSUGGEST FILTERING
        // ---------------------------------------------------------
        const filteredEquipments =
            keyw.trim().length < 2
                ? []
                : equipments.filter(e => {
                    const words = keyw.toLowerCase().split(/\s+/).filter(Boolean);

                    const fields = [
                        e.registration.toLowerCase(),
                        e.equipment.toLowerCase(),
                        e.category.toLowerCase(),
                        e.groupType.toLowerCase(),
                        e.model.toLowerCase(),
                        e.make.toLowerCase(),
                        e.idEquipment.toString()
                    ];

                    const matchesKeyword = words.every(w =>
                        fields.some(f => f.includes(w))
                    );

                    const matchesBookCapable =
                        isBookCapable === -1 ||
                        e.isBookCapable === isBookCapable;

                    const matchesIsClub =
                        isClub === -1 ||
                        e.isClub === isClub;

                    const matchesIsMant =
                        isMant === -1 ||
                        e.isMant === isMant;

                    return (
                        matchesKeyword &&
                        matchesBookCapable &&
                        matchesIsClub &&
                        matchesIsMant
                    );
                });
        useEffect(() => {
            if (filteredEquipments.length === 1) {
                const eq = filteredEquipments[0];

                // Prevent duplicates
                setSelected(prev => {
                    const exists = prev.some(x => x.idEquipment === eq.idEquipment);
                    if (exists) return prev;

                    const newList = [...prev, eq];
                    onConfirm(newList.map(x => x.idEquipment));
                    return newList;
                });

                // Optional: fill input with the selected registration
                setKeyw("");
            }
        }, [filteredEquipments]);
        // ---------------------------------------------------------
        // ADD ONE ITEM
        // ---------------------------------------------------------
        const handleAdd = (item: ItemEquipment) => {
            if (!item) return;

            // avoid duplicates
            if (selected.some(s => s.idEquipment === item.idEquipment)) return;

            const newList = [...selected, item];
            setSelected(newList);
            onConfirm(newList.map(x => x.idEquipment));
        };

        // ---------------------------------------------------------
        // REMOVE BY INDEX (Cloudscape: detail.itemIndex)
        // ---------------------------------------------------------
        const removeByIndex = (index: number) => {
            const newList = selected.filter((_, i) => i !== index);
            setSelected(newList);
            onConfirm(newList.map(x => x.idEquipment));
        };

        return (
            <SpaceBetween size="xs">
                {/* AUTOSUGGEST */}

                <Autosuggest
                    hideEnteredTextOption
                    ref={inputRef}
                    value={keyw}
                    placeholder="Buscar material"
                    filteringType="none"
                    options={filteredEquipments}
                    onChange={({ detail }) => setKeyw(detail.value)}
                    onSelect={({ detail }) => {
                        const eq = detail.selectedOption as ItemEquipment;
                        if (eq) {
                            handleAdd(eq);
                            setKeyw("");
                        }
                    }}
                />

                {/* TOKEN LIST */}
                <TokenGroup
                    items={selected.map(eq => ({
                        label: eq.registration,
                        dismissible: true
                    }))}
                    onDismiss={({ detail }) => {
                        removeByIndex(detail.itemIndex);
                    }}
                />

            </SpaceBetween>
        );
    }
);

export default SelectEquipments;
